// src/components/ThemeLandingPage.js
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "../css/okgather1.webflow.css";
import { trackThemeEvent, trackButtonClick, setUserIdProperty, registerLandingPageVariant, recordPageView, reportViewContent } from "../utils/tracking";
import { captureTrafficSource } from "../utils/sourceTracking";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import { standardUserApi } from "../services/standardUserApi";
import InviteModal from "./InviteModal";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

const ThemeLandingPage = () => {
  const { themeSlug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetupEvents, setMeetupEvents] = useState([]);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  // Invite Links: only shown to a currently-active (not trialing) paying
  // subscriber, stricter than hasActiveSubscription above which also
  // allows trialing (that's enough for RSVP-ing to meetup events, but not
  // for inviting someone else to sign up).
  const [canInvite, setCanInvite] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [attendingEventIds, setAttendingEventIds] = useState(new Set());
  const [rsvpLoadingEventId, setRsvpLoadingEventId] = useState(null);
  // Only ever set from handleAttendEvent's success path, which is itself
  // only reachable via the "Attend" button - and that button only renders
  // for an already-subscribed logged-in visitor (canRsvpDirectly below).
  // A non-subscriber/logged-out visitor's card-click still goes straight to
  // checkout/create-account exactly as before - this toast can't appear on
  // that path at all.
  const [showRsvpToast, setShowRsvpToast] = useState(false);

  // Handle Join Now button click - same destination as the homepage's Join Us button
  const handleJoinNowClick = (e) => {
    trackButtonClick(
      pageData.cta_text || 'Continue',
      e.target.closest('.cta-section') ? 'bottom_cta' : 'hero',
      {
        theme_id: pageData.theme_id,
        theme_name: pageData.theme_name,
        theme_type: pageData.theme_type,
      }
    );
  };

  // Logged-in visitors need to know if they already have an active/trialing
  // subscription before the CTA buttons below can decide whether to show at
  // all (see hideCtaForSubscriber) - a plain localStorage token check tells
  // us they're logged in immediately, but not their plan status, so that
  // has to be fetched async. Mirrors Home.js's identical check.
  useEffect(() => {
    if (!localStorage.getItem("memberstackToken")) return;
    (async () => {
      try {
        const currentMember = await memberstack.getCurrentMember();
        const planConnections = currentMember?.data?.planConnections || [];
        const hasActivePlan = planConnections.some((plan) => {
          const status = plan.status?.toLowerCase();
          const type = plan.type?.toLowerCase();
          return type === "subscription" && (status === "active" || status === "trialing");
        });
        setHasActiveSubscription(!!hasActivePlan);
        setCanInvite(planConnections.some((plan) => {
          const status = plan.status?.toLowerCase();
          const type = plan.type?.toLowerCase();
          return type === "subscription" && status === "active";
        }));
      } catch (err) {
        console.error("Memberstack DOM error:", err);
      }
    })();
  }, []);

  // Capture an invite token (?invite=TOKEN) from an Invite Links share
  // link (see GatheringInviteLink's docstring) so it can be attributed to
  // this visitor's account once they sign up - attaching happens later in
  // Checkout.js, mirroring how referralCode is carried through. Silent by
  // design: this page's conversion behavior for a normal visitor is
  // unaffected either way.
  useEffect(() => {
    const inviteToken = new URLSearchParams(window.location.search).get("invite");
    if (inviteToken) {
      const trimmed = inviteToken.trim();
      localStorage.setItem("inviteToken", trimmed);
      // Fire-and-forget: every landing hit with a token counts as a
      // click/view for that link, whether or not this visitor ever signs
      // up (see gathering_invite_link_clicks). Never blocks the page.
      standardUserApi.trackInviteLinkClick(trimmed).catch(() => {});
    }
  }, []);

  // Once we know which events are showing AND that this visitor is a
  // subscribed member (the only ones who can RSVP directly), find out
  // which of those events they've already RSVP'd to, so their button
  // renders as "Attend" or "Drop out" correctly from the start.
  //
  // Depend on a stable joined-ids string, not the meetupEvents array
  // itself: handleAttendEvent/handleDropOutEvent replace that array (via
  // .map()) to optimistically update each event's "going" count, which
  // would otherwise re-run this fetch - and re-hit the backend - on every
  // single Attend/Drop-out click even though the actual set of events on
  // screen hasn't changed.
  const meetupEventIds = useMemo(() => meetupEvents.map((e) => e.id).join(','), [meetupEvents]);
  useEffect(() => {
    const token = localStorage.getItem("memberstackToken");
    if (!token || !hasActiveSubscription || !meetupEventIds) {
      setAttendingEventIds(new Set());
      return;
    }
    standardUserApi.getMeetupEventRsvpStatus(token, meetupEventIds.split(','))
      .then((data) => setAttendingEventIds(new Set(data.attending_event_ids || [])))
      .catch(() => setAttendingEventIds(new Set()));
  }, [meetupEventIds, hasActiveSubscription]);

  const handleAttendEvent = async (eventId) => {
    const token = localStorage.getItem("memberstackToken");
    if (!token) return;
    setRsvpLoadingEventId(eventId);
    setAttendingEventIds((prev) => new Set(prev).add(eventId));
    setMeetupEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, going: e.going + 1 } : e)));
    try {
      await standardUserApi.rsvpMeetupEvent(token, eventId);
      setShowRsvpToast(true);
    } catch (err) {
      setAttendingEventIds((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
      setMeetupEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, going: Math.max(e.going - 1, 0) } : e)));
      alert("Failed to RSVP. Please try again.");
    } finally {
      setRsvpLoadingEventId(null);
    }
  };

  // Auto-dismiss the RSVP success toast a few seconds after it appears.
  useEffect(() => {
    if (!showRsvpToast) return;
    const timer = setTimeout(() => setShowRsvpToast(false), 6000);
    return () => clearTimeout(timer);
  }, [showRsvpToast]);

  const handleDropOutEvent = async (eventId) => {
    const token = localStorage.getItem("memberstackToken");
    if (!token) return;
    setRsvpLoadingEventId(eventId);
    setAttendingEventIds((prev) => {
      const next = new Set(prev);
      next.delete(eventId);
      return next;
    });
    setMeetupEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, going: Math.max(e.going - 1, 0) } : e)));
    try {
      await standardUserApi.dropOutOfMeetupEvent(token, eventId);
    } catch (err) {
      setAttendingEventIds((prev) => new Set(prev).add(eventId));
      setMeetupEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, going: e.going + 1 } : e)));
      alert("Failed to drop out. Please try again.");
    } finally {
      setRsvpLoadingEventId(null);
    }
  };

  useEffect(() => {
    const fetchLandingPage = async () => {
      try {
        setLoading(true);
        // Forward the current query string (?src=/?platform=/?placement=, admin
        // ?preview_variant=, etc.) so the backend can log/attribute correctly.
        const response = await fetch(`/api/theme-landing-pages/${themeSlug}${window.location.search}`);

        if (!response.ok) {
          if (response.status === 404) {
            navigate('/events', { replace: true });
            return;
          }
          throw new Error('Failed to load landing page');
        }

        const data = await response.json();
        setPageData(data);

        // Store the theme information in sessionStorage for use elsewhere in the app
        if (data.theme_id) {
          sessionStorage.setItem('selectedThemeId', data.theme_id);
          sessionStorage.setItem('selectedThemeName', data.theme_name || '');
          sessionStorage.setItem('selectedThemeType', data.theme_type || '');
        }

        // This landing page (e.g. a specific city or event type) may charge
        // a different amount than the site default. The slug - not the
        // price itself - is what gets carried forward (see
        // createAccountHref below and Checkout.js): Checkout.js re-fetches
        // the actual price fresh from the backend using this slug, rather
        // than trusting a cached number, so an admin changing the price
        // later, or the value going stale/missing, can't show a wrong
        // price. sessionStorage here is only a same-tab fallback for the
        // (rare) case the URL param gets lost along the way.
        sessionStorage.setItem('landingPageSlug', themeSlug);

        // Register which A/B variant was assigned before firing the view
        // event below, so it (and every later event this session) carries it.
        registerLandingPageVariant(data.variant_label);

        // Track page view with theme data
        setUserIdProperty(); // Set user ID if logged in
        trackThemeEvent('Landing Page Viewed', {
          themeId: data.theme_id,
          themeName: data.theme_name,
          themeType: data.theme_type,
          themeSlug: themeSlug
        }, {
          has_hero_image: !!data.hero_image_url,
          has_event_description: !!data.event_description,
          cta_text: data.cta_text,
          is_logged_in: !!localStorage.getItem("memberstackToken"),
          // First-touch (see sourceTracking.js's captureTrafficSource) so this
          // event - and the 'user_source' value later sent at signup/checkout -
          // agree with each other and with any earlier pageview this visitor
          // already had, rather than each landing page view re-detecting the
          // source live and possibly disagreeing on a repeat visit.
          traffic_source: captureTrafficSource(),
          referrer: document.referrer
        });
        recordPageView({
          landingPage: `theme:${themeSlug}`,
          variant: data.variant_label,
          source: captureTrafficSource(),
        });
        reportViewContent();

        // Only fetch Meetup events (and only show that section at all) when
        // this theme actually has a Meetup group linked - reset to empty
        // first so a stale list from a previously-viewed theme (client-side
        // navigation between landing pages) never bleeds through.
        if (data.meetup_urlname) {
          // Pin to the exact variant this visitor was just assigned - A/B
          // variants of the same slug can have different meetup_urlname
          // values, so without this an unrelated row for the slug could
          // get picked and show the wrong group's events (or none).
          const variantQs = data.variant_label ? `?variant=${encodeURIComponent(data.variant_label)}` : '';
          fetch(`/api/theme-landing-pages/${themeSlug}/meetup-events${variantQs}`)
            .then((res) => (res.ok ? res.json() : { events: [] }))
            .then((eventsData) => setMeetupEvents(eventsData.events || []))
            .catch(() => setMeetupEvents([]));
        } else {
          setMeetupEvents([]);
        }

        // Set SEO meta tags
        if (data.seo_title) {
          document.title = data.seo_title;
        }
        if (data.seo_description) {
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', data.seo_description);
          }
        }
      } catch (err) {
        console.error('Error fetching landing page:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLandingPage();
  }, [themeSlug, navigate]);


  if (loading) {
    return (
      <div className="theme-landing-page" style={{ backgroundColor: '#FAFAFA', minHeight: '100vh' }}>
        <style>{`
          @keyframes shimmer {
            0% {
              background-position: -1000px 0;
            }
            100% {
              background-position: 1000px 0;
            }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .shimmer {
            animation: shimmer 2s infinite;
            background: linear-gradient(
              90deg,
              #f0f0f0 0%,
              #f8f8f8 50%,
              #f0f0f0 100%
            );
            background-size: 1000px 100%;
          }
        `}</style>

        {/* Loading Hero Section */}
        <section className="hero-section" style={{
          padding: '40px 20px 30px',
          background: 'linear-gradient(135deg, #FBFAFF, #F5F2FF)',
          borderBottom: '1px solid rgba(139, 95, 191, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Spinner */}
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #8B5FBF',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 30px',
            }} />

            {/* Loading text */}
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#8B5FBF',
              margin: '0 0 12px 0',
            }}>
              Loading your experience...
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#666',
              margin: '0 0 40px 0',
            }}>
              Preparing something special
            </p>

            {/* Skeleton headline */}
            <div className="shimmer" style={{
              height: '48px',
              maxWidth: '600px',
              margin: '0 auto 20px',
              borderRadius: '8px',
            }} />

            {/* Skeleton subheadline */}
            <div className="shimmer" style={{
              height: '24px',
              maxWidth: '400px',
              margin: '0 auto 40px',
              borderRadius: '6px',
            }} />

            {/* Skeleton button */}
            <div className="shimmer" style={{
              height: '50px',
              width: '200px',
              margin: '0 auto',
              borderRadius: '25px',
            }} />
          </div>
        </section>

        {/* Loading Benefits Section */}
        <section className="benefits-section" style={{
          padding: '50px 20px',
          backgroundColor: '#FAFAFA'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '40px'
            }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{
                  padding: '30px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 95, 191, 0.1)',
                  boxShadow: '0 4px 15px rgba(139, 95, 191, 0.08)',
                }}>
                  <div className="shimmer" style={{
                    height: '24px',
                    width: '70%',
                    marginBottom: '15px',
                    borderRadius: '6px',
                  }} />
                  <div className="shimmer" style={{
                    height: '16px',
                    width: '100%',
                    marginBottom: '8px',
                    borderRadius: '4px',
                  }} />
                  <div className="shimmer" style={{
                    height: '16px',
                    width: '90%',
                    borderRadius: '4px',
                  }} />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/">Return to Home</Link>
      </div>
    );
  }

  if (!pageData) {
    return null;
  }

  // Common button styles - matches the homepage's primary "Join Us" CTA button
  const buttonStyle = {
    display: 'inline-block',
    padding: '24px 70px',
    background: 'linear-gradient(135deg, #8B5FBF, #2c2e96)',
    color: '#fff',
    textDecoration: 'none',
    borderRadius: '50px',
    fontSize: '1.4rem',
    fontWeight: '700',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 30px rgba(139, 95, 191, 0.35), 0 4px 15px rgba(139, 95, 191, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    border: 'none',
    cursor: 'pointer',
    letterSpacing: '0.5px',
    minWidth: '280px',
    textAlign: 'center',
  };

  const buttonHoverStyle = {
    background: 'linear-gradient(135deg, #9D6FD1, #2c2e96)',
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: '0 15px 45px rgba(139, 95, 191, 0.5), 0 8px 20px rgba(139, 95, 191, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
  };

  // Carried through /create-account and into /checkout as a URL param -
  // sessionStorage alone isn't reliable across that hop (ad blockers,
  // incognito storage restrictions, a new tab, etc. can all lose it), so
  // the slug travels in the URL and Checkout.js re-fetches the actual
  // price fresh rather than trusting a cached value.
  const createAccountHref = `/create-account?lp=${encodeURIComponent(themeSlug)}`;

  // "advanced" variants use the same nav banner + video hero layout as the
  // homepage instead of the plain image/text hero used by "basic" variants.
  // Hero text, video, and CTA text stay customizable per variant/landing page.
  const isAdvanced = pageData.landing_page_type === 'advanced' && !!pageData.hero_video_url;
  const isLoggedIn = !!localStorage.getItem("memberstackToken");

  // Where the CTA buttons below actually go: signed-out visitors still need
  // to create an account first; a signed-in visitor with no active/trialing
  // subscription yet should go straight to checkout instead of being sent
  // through account creation again.
  const continueHref = !isLoggedIn
    ? createAccountHref
    : `/checkout?lp=${encodeURIComponent(themeSlug)}`;

  // A signed-in subscriber already has everything this page is selling, so
  // the join/checkout CTAs (hero button, the "become a member" prompt under
  // Upcoming Events, and both bottom "Let's go!" sections) are hidden for
  // them entirely rather than pointed at /edit-profile - a "Continue"
  // button that lands on their own profile page reads as broken, not
  // helpful. hasActiveSubscription starts false until the async check above
  // resolves, so a subscriber may briefly see the join CTAs before this
  // flips true - acceptable since that check is fast and finishes well
  // before most visitors manage to click. Signed-out and non-subscribed
  // visitors are unaffected - they always see the CTAs as before.
  const hideCtaForSubscriber = isLoggedIn && hasActiveSubscription;

  return (
    <div className="theme-landing-page" style={{ backgroundColor: '#FAFAFA', minHeight: '100vh' }}>
      <style>{`
        .hero-image {
          max-width: 100%;
          height: auto;
          max-height: 250px;
          margin: 0 auto 20px;
          border-radius: 8px;
          display: block;
        }

        @media (min-width: 768px) {
          .hero-image {
            max-height: 350px;
          }
        }

        @media (min-width: 1024px) {
          .hero-image {
            max-height: 400px;
          }
        }
      `}</style>
      {showRsvpToast && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            background: '#1F2937',
            color: '#fff',
            padding: '14px 18px',
            borderRadius: '10px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            maxWidth: '90vw',
          }}
        >
          <span style={{ fontSize: '0.95rem' }}>🎉 You're going! We've saved your spot.</span>
          <Link
            to="/my-events"
            style={{ color: '#C4B5FD', fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap', textDecoration: 'none' }}
          >
            View My Events &rarr;
          </Link>
          <button
            onClick={() => setShowRsvpToast(false)}
            aria-label="Dismiss"
            style={{
              background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer',
              fontSize: '1.1rem', lineHeight: 1, padding: '0 0 0 4px',
            }}
          >
            &times;
          </button>
        </div>
      )}
      {isLoggedIn && canInvite && (
        <button
          onClick={() => setShowInviteModal(true)}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 1200,
            padding: '14px 22px',
            borderRadius: '100px',
            border: 'none',
            backgroundColor: '#25D366',
            color: '#fff',
            fontWeight: '700',
            fontSize: '0.95rem',
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(37, 211, 102, 0.45)',
            transition: 'transform 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          🔗 Invite someone
        </button>
      )}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        themeId={pageData.theme_id}
        themeName={pageData.theme_name}
        gatheringId={null}
        gatheringDateLabel={null}
      />
      {isAdvanced && (
        <>
          {/* Same responsive rules as the homepage's hero video, so it frames
              and positions identically at every breakpoint (see Home.js). */}
          <style>{`
            @media (min-width: 1200px) {
              .section_hero { min-height: 50vh !important; max-height: 600px !important; }
              .section_hero video { object-position: center 25% !important; }
            }
            @media (max-width: 1199px) and (min-width: 769px) {
              .section_hero { min-height: 45vh !important; }
              .section_hero video { object-position: center 30% !important; }
            }
            @media (max-width: 768px) {
              .nav-content { flex-wrap: wrap; gap: 10px; }
              .nav-signup-button, .nav-login-button { padding: 8px 16px !important; font-size: 0.85rem !important; }
              .section_hero { min-height: 40vh !important; max-height: 350px !important; }
              .section_hero video { object-position: 40% 20% !important; }
              .hero-cta { padding: 22px 50px !important; font-size: 1.3rem !important; min-width: 260px !important; }
            }
            @media (max-width: 480px) {
              .nav-content { padding: 12px 0 !important; }
              .nav-logo { font-size: 1.4rem !important; }
              .nav-signup-button, .nav-login-button { padding: 6px 12px !important; font-size: 0.8rem !important; }
              .section_hero { min-height: 35vh !important; max-height: 300px !important; }
              .section_hero video { object-position: 45% 15% !important; }
              .hero-cta { padding: 20px 45px !important; font-size: 1.25rem !important; min-width: 240px !important; }
            }
            @supports (aspect-ratio: 16 / 9) {
              .section_hero video { aspect-ratio: 16 / 9; object-fit: cover; }
            }
          `}</style>

          {/* Top Navigation Banner - matches the homepage exactly */}
          <section
            className="nav-banner"
            style={{
              background: "linear-gradient(135deg, #FBFAFF, #F5F2FF)",
              borderBottom: "1px solid rgba(139, 95, 191, 0.08)",
              position: "sticky",
              top: 0,
              zIndex: 100,
            }}
          >
            <div className="padding-global">
              <div className="container-large">
                <div
                  className="nav-content"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "18px 0",
                  }}
                >
                  <a
                    href="/"
                    className="nav-logo"
                    style={{
                      fontSize: "2rem",
                      fontWeight: "800",
                      // Homepage logo is an <h1> (gets stylesheet's h1 { line-height: 44px }).
                      // This is a plain <a> so it'd otherwise inherit body's 20px line-height,
                      // clipping the "g" descender in the gradient-clipped text. Match the h1 value.
                      lineHeight: "44px",
                      background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      margin: 0,
                      textDecoration: "none",
                      display: "inline-block",
                    }}
                  >
                    OkGather
                  </a>

                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    {isLoggedIn ? (
                      <>
                        {hasActiveSubscription && (
                          <a
                            href="/my-events"
                            style={{
                              background: "transparent",
                              color: "#8B5FBF",
                              padding: "8px 16px",
                              borderRadius: "20px",
                              textDecoration: "none",
                              fontSize: "0.9rem",
                              fontWeight: "600",
                              border: "2px solid #8B5FBF",
                              transition: "all 0.3s ease",
                            }}
                          >
                            My Events
                          </a>
                        )}
                        <a
                          href="/edit-profile"
                          style={{
                            background: "transparent",
                            color: "#8B5FBF",
                            padding: "8px 16px",
                            borderRadius: "20px",
                            textDecoration: "none",
                            fontSize: "0.9rem",
                            fontWeight: "600",
                            border: "2px solid #8B5FBF",
                            transition: "all 0.3s ease",
                          }}
                        >
                          My Profile
                        </a>
                      </>
                    ) : (
                      <>
                        <a
                          href={createAccountHref}
                          className="nav-signup-button"
                          onClick={handleJoinNowClick}
                          style={{
                            background: "linear-gradient(135deg, #8B5FBF, #2c2e96)",
                            color: "#fff",
                            padding: "12px 24px",
                            borderRadius: "25px",
                            textDecoration: "none",
                            fontSize: "0.95rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            boxShadow: "0 2px 10px rgba(139, 95, 191, 0.2)",
                            border: "none",
                          }}
                        >
                          Join Us
                        </a>
                        <a
                          href="/login"
                          className="nav-login-button"
                          style={{
                            background: "transparent",
                            color: "#8B5FBF",
                            padding: "12px 24px",
                            borderRadius: "25px",
                            textDecoration: "none",
                            fontSize: "0.95rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            border: "2px solid #8B5FBF",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "#8B5FBF";
                            e.target.style.color = "#fff";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "transparent";
                            e.target.style.color = "#8B5FBF";
                          }}
                        >
                          Login
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Hero Section - same video/layout as the homepage; headline,
              subheadline, video, and CTA text are this variant's own. */}
          <section
            className="section_hero"
            style={{
              position: "relative",
              minHeight: "30vh",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              poster="/images/hero-poster.jpg"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 30%",
                zIndex: 1,
              }}
            >
              <source src={pageData.hero_video_url} type="video/mp4" />
            </video>

            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, rgba(139, 95, 191, 0.3), rgba(107, 70, 193, 0.3)), linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.1) 70%, rgba(0, 0, 0, 0.3) 100%)`,
                zIndex: 2,
              }}
            />

            <div className="padding-global padding-section-large" style={{ position: "relative", zIndex: 3, width: "100%" }}>
              <div className="container-large">
                <div className="hero_content text-align-center">
                  {pageData.hero_headline && (
                    <h1
                      className="hero-headline"
                      style={{
                        fontSize: "clamp(2rem, 5vw, 3.2rem)",
                        fontWeight: "800",
                        color: "#fff",
                        textShadow: "2px 2px 8px rgba(0, 0, 0, 0.6)",
                        lineHeight: "1.1",
                        maxWidth: "900px",
                        margin: "40px auto 16px",
                        textAlign: "center",
                      }}
                    >
                      {pageData.hero_headline}
                    </h1>
                  )}

                  {pageData.hero_subheadline && (
                    <p style={{
                      color: "#fff",
                      fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
                      textShadow: "1px 1px 4px rgba(0,0,0,0.5)",
                      margin: "0 auto 30px",
                      maxWidth: "600px",
                      textAlign: "center",
                      fontWeight: "500",
                    }}>
                      {pageData.hero_subheadline}
                    </p>
                  )}

                  {!hideCtaForSubscriber && (
                    <div className="cta-buttons" style={{
                      marginTop: "10px",
                      marginBottom: "35px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}>
                      <a href={continueHref}
                        onClick={handleJoinNowClick}
                        className="button-2 is-primary cta-button hero-cta"
                        style={buttonStyle}
                        onMouseEnter={(e) => { Object.assign(e.target.style, buttonHoverStyle); }}
                        onMouseLeave={(e) => { Object.assign(e.target.style, buttonStyle); }}
                      >
                        {pageData.cta_text || 'Continue'}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Basic variant has no nav banner of its own, so a subscriber
          visiting this page still gets quick links back to their account -
          matches the advanced variant's nav banner links above. Logged-out
          visitors and non-subscribers see nothing extra here, so this
          never touches this page's conversion-facing content. */}
      {!isAdvanced && isLoggedIn && hasActiveSubscription && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            padding: "12px 20px",
            background: "#FBFAFF",
            borderBottom: "1px solid rgba(139, 95, 191, 0.1)",
          }}
        >
          <a
            href="/my-events"
            style={{
              background: "transparent",
              color: "#8B5FBF",
              padding: "8px 16px",
              borderRadius: "20px",
              textDecoration: "none",
              fontSize: "0.9rem",
              fontWeight: "600",
              border: "2px solid #8B5FBF",
            }}
          >
            My Events
          </a>
          <a
            href="/edit-profile"
            style={{
              background: "transparent",
              color: "#8B5FBF",
              padding: "8px 16px",
              borderRadius: "20px",
              textDecoration: "none",
              fontSize: "0.9rem",
              fontWeight: "600",
              border: "2px solid #8B5FBF",
            }}
          >
            My Profile
          </a>
        </div>
      )}

      {/* Basic Hero Section */}
      {!isAdvanced && (
      <section className="hero-section" style={{
        padding: '40px 20px 30px',
        background: 'linear-gradient(135deg, #FBFAFF, #F5F2FF)',
        borderBottom: '1px solid rgba(139, 95, 191, 0.1)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {pageData.hero_image_url && (
            <img
              src={pageData.hero_image_url}
              alt={pageData.hero_headline}
              className="hero-image"
            />
          )}
          {pageData.hero_headline && (
            <h1 style={{
              fontSize: 'clamp(1.75rem, 4.5vw, 2.75rem)',
              fontWeight: '800',
              marginBottom: '15px',
              marginTop: pageData.hero_image_url ? '0' : '0',
              color: '#1a1a1a',
              lineHeight: '1.2',
              paddingBottom: '0.1em'
            }}>
              {pageData.hero_headline}
            </h1>
          )}
          {pageData.hero_subheadline && (
            <p style={{
              fontSize: '24px',
              color: '#666',
              marginBottom: '25px'
            }}>
              {pageData.hero_subheadline}
            </p>
          )}
          {!hideCtaForSubscriber && (
            <a
              href={continueHref}
              onClick={handleJoinNowClick}
              className="button-primary"
              style={buttonStyle}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, buttonHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.target.style, buttonStyle);
              }}
            >
              {pageData.cta_text || 'Continue'}
            </a>
          )}
        </div>
      </section>
      )}

      {/* Upcoming Events Section - only present when this theme has a Meetup
          group linked and that group has upcoming events; otherwise this
          renders nothing (no empty-state placeholder). */}
      {meetupEvents.length > 0 && (
        <section className="upcoming-events-section" style={{
          padding: '50px 20px',
          backgroundColor: 'white',
          borderBottom: '1px solid rgba(139, 95, 191, 0.1)'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #8B5FBF, #6B46C1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '30px',
              textAlign: 'center',
              lineHeight: '1.2',
              paddingBottom: '0.1em'
            }}>
              Upcoming Events
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px'
            }}>
              {meetupEvents.map((event, index) => {
                const canRsvpDirectly = isLoggedIn && hasActiveSubscription;
                const isAttending = attendingEventIds.has(event.id);
                const isUpdating = rsvpLoadingEventId === event.id;
                return (
                <div key={event.id} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div
                    className="upcoming-event-card"
                    onClick={canRsvpDirectly ? undefined : () => navigate(!isLoggedIn ? createAccountHref : `/checkout?lp=${encodeURIComponent(themeSlug)}`)}
                    style={{
                      padding: '24px',
                      backgroundColor: '#FAFAFA',
                      borderRadius: '12px',
                      border: '1px solid rgba(139, 95, 191, 0.1)',
                      cursor: canRsvpDirectly ? 'default' : 'pointer',
                      flex: '1 1 auto',
                    }}
                  >
                    <h3 style={{
                      fontSize: '1.15rem',
                      fontWeight: '700',
                      color: '#333',
                      marginBottom: '10px'
                    }}>
                      {event.title}
                    </h3>
                    <p style={{
                      fontSize: '0.95rem',
                      color: '#8B5FBF',
                      fontWeight: '600',
                      marginBottom: '6px'
                    }}>
                      {new Date(event.starts_at).toLocaleString('en-GB', {
                        weekday: 'short', day: 'numeric', month: 'short',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                    {event.venue_name && (
                      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '2px' }}>
                        {event.venue_name}
                      </p>
                    )}
                    {(event.venue_address || event.venue_city) && (
                      <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '6px' }}>
                        {[event.venue_address, event.venue_city].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {event.going >= 30 && (
                      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '6px' }}>
                        {event.going} going
                      </p>
                    )}
                    {index === 0 && event.spaces_left != null && event.spaces_left < 15 && (
                      <p style={{ fontSize: '0.85rem', color: '#c0392b', fontWeight: '600', margin: 0 }}>
                        Limited spaces left
                      </p>
                    )}
                    {!canRsvpDirectly && (
                      <p style={{ fontSize: '0.85rem', color: '#8B5FBF', fontWeight: '600', margin: '10px 0 0 0' }}>
                        Join to RSVP &rarr;
                      </p>
                    )}
                  </div>
                  {canRsvpDirectly && (
                    isAttending ? (
                      <button
                        onClick={() => handleDropOutEvent(event.id)}
                        disabled={isUpdating}
                        style={{
                          width: '100%',
                          marginTop: '10px',
                          padding: '10px',
                          borderRadius: '8px',
                          border: 'none',
                          backgroundColor: '#D1D5DB',
                          color: '#374151',
                          fontWeight: '600',
                          cursor: isUpdating ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isUpdating ? 'Updating...' : 'Drop out'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAttendEvent(event.id)}
                        disabled={isUpdating}
                        style={{
                          width: '100%',
                          marginTop: '10px',
                          padding: '10px',
                          borderRadius: '8px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #8B5FBF, #6B46C1)',
                          color: '#fff',
                          fontWeight: '600',
                          cursor: isUpdating ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isUpdating ? 'Updating...' : 'Attend'}
                      </button>
                    )
                  )}
                </div>
                );
              })}
            </div>
            {!hideCtaForSubscriber && (
              <>
                <p style={{
                  fontSize: '1.05rem',
                  fontWeight: '600',
                  color: '#333',
                  textAlign: 'center',
                  marginTop: '24px',
                  maxWidth: '700px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  lineHeight: '1.6'
                }}>
                  To attend, simply become a member now (only takes 1 minute to join) and you'll also
                  get 20% off food &amp; drinks at the gathering (show your OkGather profile at the bar).
                </p>
                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                  <a
                    href={continueHref}
                    onClick={handleJoinNowClick}
                    className="button-primary"
                    style={buttonStyle}
                    onMouseEnter={(e) => {
                      Object.assign(e.target.style, buttonHoverStyle);
                    }}
                    onMouseLeave={(e) => {
                      Object.assign(e.target.style, buttonStyle);
                    }}
                  >
                    {pageData.cta_text || 'Continue'}
                  </a>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Event Description Section */}
      {pageData.event_description && (
        <section className="description-section" style={{
          padding: '40px 20px',
          backgroundColor: 'white'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{
              fontSize: '18px',
              lineHeight: '1.8',
              color: '#333',
              whiteSpace: 'pre-wrap'
            }}>
              {pageData.event_description}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="benefits-section" style={{
        padding: '50px 20px',
        backgroundColor: '#FAFAFA'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px'
          }}>
            {/* Benefit 1 */}
            {(pageData.benefit_1_title || pageData.benefit_1_text) && (
              <div className="benefit-card" style={{
                padding: '30px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(139, 95, 191, 0.1)',
                boxShadow: '0 4px 15px rgba(139, 95, 191, 0.08)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}>
                {pageData.benefit_1_title && (
                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '15px',
                    color: '#8B5FBF'
                  }}>
                    {pageData.benefit_1_title}
                  </h3>
                )}
                {pageData.benefit_1_text && (
                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: '#666'
                  }}>
                    {pageData.benefit_1_text}
                  </p>
                )}
              </div>
            )}

            {/* Benefit 2 */}
            {(pageData.benefit_2_title || pageData.benefit_2_text) && (
              <div className="benefit-card" style={{
                padding: '30px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(139, 95, 191, 0.1)',
                boxShadow: '0 4px 15px rgba(139, 95, 191, 0.08)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}>
                {pageData.benefit_2_title && (
                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '15px',
                    color: '#8B5FBF'
                  }}>
                    {pageData.benefit_2_title}
                  </h3>
                )}
                {pageData.benefit_2_text && (
                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: '#666'
                  }}>
                    {pageData.benefit_2_text}
                  </p>
                )}
              </div>
            )}

            {/* Benefit 3 */}
            {(pageData.benefit_3_title || pageData.benefit_3_text) && (
              <div className="benefit-card" style={{
                padding: '30px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(139, 95, 191, 0.1)',
                boxShadow: '0 4px 15px rgba(139, 95, 191, 0.08)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}>
                {pageData.benefit_3_title && (
                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '15px',
                    color: '#8B5FBF'
                  }}>
                    {pageData.benefit_3_title}
                  </h3>
                )}
                {pageData.benefit_3_text && (
                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: '#666'
                  }}>
                    {pageData.benefit_3_text}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Description Section */}
      {pageData.description && (
        <section className="description-section" style={{
          padding: '50px 20px',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#333'
          }}>
            <div
              dangerouslySetInnerHTML={{ __html: pageData.description }}
              style={{
                padding: '0'
              }}
            />
          </div>
        </section>
      )}

      {/* CTA Section */}
      {!hideCtaForSubscriber && (
        <section className="cta-section" style={{
          padding: '50px 20px',
          background: 'linear-gradient(135deg, #FBFAFF, #F5F2FF)',
          borderTop: '1px solid rgba(139, 95, 191, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '800',
              marginBottom: '25px',
              background: 'linear-gradient(135deg, #8B5FBF, #6B46C1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: '1.2',
              paddingBottom: '0.1em'
            }}>
              {pageData.cta_heading || 'Let\'s go!'}
            </h2>
            <a
              href={continueHref}
              onClick={handleJoinNowClick}
              className="button-primary"
              style={buttonStyle}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, buttonHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.target.style, buttonStyle);
              }}
            >
              {pageData.cta_text || 'Continue'}
            </a>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section
        style={{
          backgroundColor: "#fff",
          padding: "50px 20px",
          borderTop: "1px solid rgba(139, 95, 191, 0.1)",
        }}
      >
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "800",
              background: "linear-gradient(135deg, #8B5FBF, #6B46C1)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              marginBottom: "30px",
              textAlign: "center",
            }}
          >
            How It Works
          </h2>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            {[
              { num: "1", title: "Become a Member", desc: "Must be a member to attend events.", icon: "✨" },
              { num: "2", title: "Attend Events", desc: "Access all gatherings as many times as you like.", icon: "📅" },
              { num: "3", title: "Get 20% Off Food & Drinks", desc: "Show your member profile at the bar and enjoy 20% off food and drinks at the venue.", icon: "🍹" },
            ].map((step, index, array) => (
              <React.Fragment key={step.num}>
                <div
                  style={{
                    backgroundColor: "#FAFAFA",
                    borderRadius: "16px",
                    padding: "32px 24px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "2.5rem",
                      marginBottom: "12px",
                    }}
                  >
                    {step.icon}
                  </div>
                  <h3
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      color: "#333",
                      marginBottom: "8px",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontSize: "0.95rem",
                      color: "#666",
                      margin: 0,
                      lineHeight: "1.5",
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
                {index < array.length - 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "16px 0",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        backgroundColor: "#E9D5FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(139, 95, 191, 0.15)",
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5V19M12 19L6 13M12 19L18 13"
                          stroke="#8B5FBF"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Second CTA Section */}
      {!hideCtaForSubscriber && (
        <section className="cta-section" style={{
          padding: '50px 20px',
          background: 'linear-gradient(135deg, #FBFAFF, #F5F2FF)',
          borderTop: '1px solid rgba(139, 95, 191, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '800',
              marginBottom: '25px',
              background: 'linear-gradient(135deg, #8B5FBF, #6B46C1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: '1.2',
              paddingBottom: '0.1em'
            }}>
              {pageData.cta_heading || 'Let\'s go!'}
            </h2>
            <a
              href={continueHref}
              onClick={handleJoinNowClick}
              className="button-primary"
              style={buttonStyle}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, buttonHoverStyle);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.target.style, buttonStyle);
              }}
            >
              {pageData.cta_text || 'Continue'}
            </a>
          </div>
        </section>
      )}
    </div>
  );
};

export default ThemeLandingPage;
